/**
 * "Accept and commit" confirmation (f10.4, design D10 section 5): what the
 * commit will create in Administration, and which later steps become stale.
 * Every number is the server's `commit_plan`.
 */

import { Check, Info } from 'lucide-react';

import type { UsEngagementsReview } from '@/api/client';
import { Modal } from '@/routes/reference-data/Modal';

import { refusalMessage } from './usEngagementsView';

export function CommitDialog({
  review,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  review: UsEngagementsReview;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const plan = review.commit_plan;
  const stale = plan.later_steps_becoming_stale;
  return (
    <Modal
      title="Accept and commit?"
      subtitle={`US Engagements master · ${review.batch.original_filename ?? `batch #${review.batch.batch_number}`}`}
      onClose={onClose}
      onSubmit={onConfirm}
      busy={busy}
      submitLabel="Accept and commit"
      submitIcon={<Check aria-hidden="true" />}
      layout="plain"
      message={refusalMessage(error)}
    >
      <dl className="dl cols-2 mb-4">
        <div>
          <dt>Clients created</dt>
          <dd>{plan.clients_created}</dd>
        </div>
        <div>
          <dt>Engagements created</dt>
          <dd>{plan.engagements_created}</dd>
        </div>
        <div>
          <dt>Elements confirmed</dt>
          <dd>{plan.elements_confirmed}</dd>
        </div>
        <div>
          <dt>Aliases added</dt>
          <dd>{plan.aliases_added}</dd>
        </div>
        <div>
          <dt>SAP IDs recorded</dt>
          <dd>{plan.sap_ids_recorded}</dd>
        </div>
        <div>
          <dt>Step after commit</dt>
          <dd>Complete</dd>
        </div>
        <div>
          <dt>Later completed steps become stale</dt>
          <dd>{stale.length ? stale.join(', ') : 'None'}</dd>
        </div>
      </dl>
      <div className="alert alert-info">
        <Info aria-hidden="true" />
        <div className="a-body">
          The commit runs as a background job and applies everything in one transaction through
          Administration &mdash; all of it, or nothing.
        </div>
      </div>
    </Modal>
  );
}
